import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MultipleSortSettings } from '@progress/kendo-angular-grid';
import { ContextMenuComponent } from "@progress/kendo-angular-menu";
import { SortDescriptor } from "@progress/kendo-data-query";
import { CurrentUserService } from '../../../security/current-user.service';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { CollectionDetailDialog } from '../../dialogs/collection-detail/collection-detail.dialog';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { ImportSiteDialog } from '../../dialogs/import-site/import-site.dialog';
import { SiteGridRemoteBindingDirective } from '../../directives/site-grid-remote-binding.directive';
import { KendoGridHelper } from '../../kendo-grid-helper';
import { IMenuItem } from '../../models/form-template-instances/menu-item.models';
import { DataCollection } from '../../models/site-content/data-collection.model';
import { ProgressConfig } from '../../services/progress-indicator.service';
import { SiteGridStateInfo, SiteListColumnDef } from './extras';

const Open_MenuItem: string = "Open";
const CopyLink_MenuItem: string = "Copy Link";

@Component({
    selector: 'app-site-grid',
    templateUrl: './site-grid.component.html',
    styleUrls: ['./site-grid.component.scss'],
    encapsulation: ViewEncapsulation.None, //enable CSS overrides
    //encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class SiteGridComponent implements OnInit {
    // Get a reference to the remote binding directive that handles all the changes to Grid state (Relates to <kendo-grid #grid="remoteBindingDirectiveRef" in the HTML)
    @ViewChild(SiteGridRemoteBindingDirective) public remoteBindingDirectiveRef: SiteGridRemoteBindingDirective;
    @ViewChild("gridmenu") private gridContextMenu: ContextMenuComponent;
    private helper = new KendoGridHelper();
    private kendoGridData: SiteGridStateInfo = new SiteGridStateInfo();
    private showAllForAdmin: boolean = false;

    public columnDefs: SiteListColumnDef[] = [
        { fieldName: 'name', title: 'Name', width: '200' },
        { fieldName: 'lastAccessedDate', title: 'Last Accessed by You', width: '75' },
        { fieldName: 'description', title: 'Description', width: '75' },
        { fieldName: 'purpose', title: 'BDR/Memo Number and Title', width: '75' },
        { fieldName: 'currentWorkflowState', title: 'Status', width: '75' },
        { fieldName: 'id', title: 'ID', width: '100' },
        { fieldName: 'isFavorite', title: 'Fav', width: '0' }
    ];

    private readonly menuItems: IMenuItem[] =
        [
            { text: Open_MenuItem },
            { text: CopyLink_MenuItem },
        ];

    public get SortSettings(): any {
        let settings: MultipleSortSettings = {
            //mode: "multiple",
            initialDirection: "asc",
            allowUnsort: true,
            showIndexes: true,
        };
        return settings;
    }

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private clipboard: Clipboard,
        private dataCollectionService: DataCollectionService,
        private currentUserService: CurrentUserService) { }

    ngOnInit(): void {
    }

    public openSite(event, dataItem): void {
        if (event.shiftKey) {
            this.router.navigateByUrl(`/site/${dataItem.id}`);
        }
        else {
            window.open(`/site/${dataItem.id}`, '_blank').focus();
        }
    }

    public deleteSite(event, dataItem): void {
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: "400px",
            data: new ConfirmationDialogModel(
                `Delete Site`,
                `Are you sure you want to delete the Site "${dataItem.name}"?`
            )
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                this.dataCollectionService.delete(dataItem.id).then(x => {
                    this.remoteBindingDirectiveRef.rebind();
                });
            }
        });
    }

    /**
     * *Makes a site active or inActive.  If a site is inActive, it cannot be viewed by ANY users
     * @param dataItem - Column and row value from the grid
     */
    public toggleActivation(dataItem: any): void {
        //***Check if this is admin list first*/
        if (this.currentUserService.user.isSystemAdmin) {
            dataItem.inActive = !dataItem.inActive;
            this.dataCollectionService.toggleInActive(dataItem.id).then(fi => { });
        }
    }

    public toggleFavorite(dataItem: any): void {
        dataItem.isFavorite = !dataItem.isFavorite;
        this.dataCollectionService.toggleIsFavorite(dataItem.id).then(fi => { });
    }

    public toggleSiteAdmin(dataItem: any): void {
        dataItem.siteIsAdministerable = !dataItem.siteIsAdministerable;
        if (dataItem.siteIsAdministerable) {
            this.dataCollectionService.addAsSiteAdmin(dataItem.id).then(fi => { });
        } else {
            this.dataCollectionService.removeAsSiteAdmin(dataItem.id).then(fi => { });
        }
    }

    public showActivationForAdminIcon(dataItem: any): string {
        if (!dataItem.inActive) {
            return "check_circle";
        } else {
            return "unpublished";
        }
    }

    public favIcon(dataItem: any): string {
        if (dataItem.isFavorite) {
            return "star";
        } else {
            return "star_outline";
        }
    }

    public showAllForAdminIcon(dataItem: any): string {
        if (dataItem.siteIsAdministerable) {
            return "person";
        } else {
            return "person_outline";
        }
    }

    public toggleShowAllForAdmin() {
        this.showAllForAdmin = !this.showAllForAdmin;
        this.remoteBindingDirectiveRef.ShowAllForAdmin = this.showAllForAdmin;
        this.remoteBindingDirectiveRef.rebind();
    }

    public handleNewSite(): void {
        const dialogRef = this.dialog.open(CollectionDetailDialog, {
            width: '60%',
            data: {}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                let collection = new DataCollection();

                collection.name = result.name;

                this.dataCollectionService.create(
                    collection,
                    new ProgressConfig(`Creating new Site "${collection.name}"`, `Done creating Site "${collection.name}"`))
                    .then(response => {
                        this.router.navigate(['/site/' + response.id]);
                    });
            }
        });
    }

    public handleSiteImport(): void {
        // Process an export site dialogue.

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';

        const dialogRef = this.dialog.open(ImportSiteDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(importedSiteId => {
            // Note:  the site import is performed by the dialogue.
            // If the site Id is returned, load the copied site.
            if (importedSiteId != null) {
                this.dataCollectionService.get(importedSiteId)
                    .then(response => {
                        this.router.navigate(['/site/' + response.id]);
                    });
            }
        });

        return;
    }

    public onCellClick(e: any): void {
        // Call the original code associated with a cell click.
        this.helper.setIndexOfClickedRow(e);
        //this.rowIndexClicked = e.rowIndex;

        // If this event is of type 'contextmenu', handle that.
        if (e.type === "contextmenu") {
            const originalEvent = e.originalEvent;
            originalEvent.preventDefault();

            this.helper.ContextMenuDataItem = e.dataItem;

            // Show the context menu.
            this.gridContextMenu.show({
                left: originalEvent.pageX,
                top: originalEvent.pageY,
            });
        }
    }

    public onContextMenuSelect(eventData: any): void {
        if ((this.helper.ContextMenuDataItem != null) && (eventData['item'] != null) && (eventData['item']['text'] != null)) {
            let menuItemText: string = eventData['item']['text'];
            switch (menuItemText) {
                case Open_MenuItem:
                    this.openSite(eventData, this.helper.ContextMenuDataItem);
                    break;
                case CopyLink_MenuItem:
                    let url: string = `${location.origin}/site/${this.helper.ContextMenuDataItem?.id}`;
                    this.clipboard.copy(url);
                    break;
                default:
                    break;
            }
        }
    }

    public get ShowAllForAdminButton(): boolean {
        return this.currentUserService.user.isSystemAdmin;
    }
    public get ShowDeleteButtons(): boolean {
        return this.currentUserService.user.isSystemAdmin;
    }

    public get CommandColumnWidth(): number {
        return this.ShowDeleteButtons ? 82 : 55;
    }

    public get MenuItems(): IMenuItem[] {
        return this.menuItems;
    }

    // Getters / Setters
    public get Helper(): KendoGridHelper {
        return this.helper;
    }

    public get ShowAllForAdmin(): boolean {
        return this.showAllForAdmin;
    }

    public get ShowAllForAdminButtonText(): string {
        return this.showAllForAdmin ? "Show only Sites you have access to" : "Show All for Admin";
    }

    public get ColumnDefs() {
        return this.columnDefs.filter(x => { return x.fieldName != 'isFavorite' });
    }

    public get FavoriteColumnDef() {
        let faveCol = this.columnDefs.find(x => { return x.fieldName == 'isFavorite' });
        return faveCol;
    }

    // Getters for virtual scrolling
    public get PageSize(): number {
        return this.kendoGridData.gridState.take;
    }

    public get Skip(): number {
        return this.kendoGridData.gridState.skip;
    }
    public get Sort(): SortDescriptor[] {
        return this.kendoGridData.gridState.sort;
    }

}
